import type { Channel } from "./channel";
import type { User } from "./user";
import type { AgentTraceStep } from "@/features/ai-agent/types/agent";
import type { MessageGenerationStep } from "@/features/ai-agent/types/agent-step";
import type { SendWhatsappTemplatePayload } from "./whatsapp/send-template";
import type { ResolvedWhatsappTemplatePayload } from "./whatsapp/template";

export const OWNER = {
  CONTACT: "contact",
  USER: "user",
  API: "api",
  BROADCAST: "broadcast",
  ACCOUNT: "account",
  AI: "ai",
} as const;

export type Owner = (typeof OWNER)[keyof typeof OWNER];

export const MESSAGE_TYPE = {
  TEXT: "text",
  COMMENT: "comment",
  ATTACHMENT: "attachment",
  QUICK_REPLY: "quick_reply",
  TEMPLATE: "template",
  CAROUSEL_TEMPLATE: "carousel_template",
  UNSUPPORTED: "unsupported",
} as const;

export type MessageType = (typeof MESSAGE_TYPE)[keyof typeof MESSAGE_TYPE];

export const ATTACHMENT_TYPE = {
  IMAGE: "image",
  VIDEO: "video",
  AUDIO: "audio",
  FILE: "file",
  IFRAME: "iframe",
  STICKER: "sticker",
} as const;

export type AttachmentType =
  (typeof ATTACHMENT_TYPE)[keyof typeof ATTACHMENT_TYPE];

export const SEND_EVENT_MESSAGE_TYPE = {
  TEXT: "text",
  IMAGE: "image",
  FILE: "file",
  VIDEO: "video",
  AUDIO: "audio",
  SHARE: "share",
  TEMPLATE: "template",
  QUICK_REPLY: "quick_reply",
} as const;

export type SendEventMessageType =
  (typeof SEND_EVENT_MESSAGE_TYPE)[keyof typeof SEND_EVENT_MESSAGE_TYPE];

export const MESSAGE_STATUS = {
  SENDING: "sending",
  RECEIVED: "received",
  PENDING: "pending",
  SENT: "sent",
  DELIVERED: "delivered",
  READ: "read",
  FAILED: "failed",
} as const;

export type MessageStatus =
  (typeof MESSAGE_STATUS)[keyof typeof MESSAGE_STATUS];

export const ATTACHMENT_SOURCE = {
  STORY: "story",
  STORY_SHARE: "story_share", // when someone sends a story
  STORY_MENTION: "story_mention",
  STORY_REPLY: "story_reply",
  REEL: "reel",
  POST: "post",
  CONVERSATION: "conversation",
} as const;

export type AttachmentSource =
  (typeof ATTACHMENT_SOURCE)[keyof typeof ATTACHMENT_SOURCE];

export const ATTACHMENT_STATUS = {
  PENDING: "pending",
  READY: "ready",
} as const;

export type AttachmentStatus =
  (typeof ATTACHMENT_STATUS)[keyof typeof ATTACHMENT_STATUS];

export const FOOTER_INPUT_TYPE = {
  MESSAGE: "message",
  COMMENT: "comment",
} as const;

export type FooterInputType =
  (typeof FOOTER_INPUT_TYPE)[keyof typeof FOOTER_INPUT_TYPE];

// Interfaces
export type MessageReaction = {
  id: string;
  emoji: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type MessageEditMeta = {
  count?: number;
};

export type MessageMeta = {
  edit?: MessageEditMeta | null;
  agentSteps?: AgentTraceStep[];
};

export type ClickedAd = {
  id: string;
  externalId: string;
  name: string;
  description: string | null;
  videoUrl: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  productId: string | null;
  source: string;
  mediaId: string | null;
  createdAt: string;
  updatedAt: string;
  adTitle?: string | null;
};

export type QuickReplyButton = {
  text: string;
  payload: string;
};

export type QuickReplyPayload = {
  text: string;
  buttons: QuickReplyButton[];
};

export type Message = {
  id: string;
  text: null | string;
  owner: Owner;
  /** AI drafted/assisted the content with a human in the loop before it was
   *  sent — rendered with both the AI and the human agent avatars. */
  isAiAssisted?: boolean;
  externalId: string;
  error?: MessageError | null;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  receivedAt?: string | null;
  statuses: MessageStatusRecord[];
  attachments: Attachment[];
  reactions: MessageReaction[];
  user: User | null;
  type: MessageType;
  channel: Pick<Channel, "id">;
  linked: Message | null;
  templateId?: string;
  payload?: ResolvedWhatsappTemplatePayload | QuickReplyPayload;
  clickedAd?: ClickedAd | null;
  meta?: MessageMeta | null;
  /** AI-run step timeline embedded on the inbound message (oldest first) so the
   *  agent-activity panel rehydrates on refresh. Empty when the message had no
   *  run; seeded into the agent-steps store on conversation load. */
  steps?: MessageGenerationStep[] | null;
};

export type Attachment = {
  id: string;
  url: string;
  name: null | string;
  caption: null | string;
  externalId: string;
  type: AttachmentType;
  createdAt: Date;
  updatedAt: Date;
  contentType: string;
  source: AttachmentSource;
  status?: AttachmentStatus;
};

export type MessageError = {
  id: string | null;
  code: string | null;
  message: string | null;
  retry: boolean | null;
  subCode: string | null;
};

// Renamed from MessageStatus interface to avoid conflict with MessageStatus type
export type MessageStatusRecord = {
  id: string;
  status: MessageStatus;
  createdAt: Date;
  updatedAt: Date;
  receivedAt: Date | null;
  messageId: string;
};

export type SendAttachmentPayload = {
  id: string;
  name?: string;
  url: string;
  caption?: string;
  contentType?: string;
};

export type SendMessagePayload = {
  id?: string;
  channelId: string;
  contactId: string;
  type: SendEventMessageType;
  text?: string;
  image?: SendAttachmentPayload;
  file?: SendAttachmentPayload;
  video?: SendAttachmentPayload;
  audio?: SendAttachmentPayload;
  share?: SendAttachmentPayload;
  replyTo?: string;
  template?: {
    id: string;
    variables: SendWhatsappTemplatePayload;
  };
  quickReply?: QuickReplyPayload;
  isAiAssisted?: boolean;
};

export type SendCommentPayload = {
  channelId: string;
  contactId: string;
  id?: string;
  text: string;
  replyTo: string;
};
